/**
 * DealProvider Component
 *
 * A context provider that manages deal state and operations for child components.
 * Provides a unified interface for deal management across the application.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react'
import { useDealCRUD, DealCRUDConfig } from '../hooks/useDealCRUD'
import {
  Deal,
  CreateDealInput,
  UpdateDealInput,
  DealContextValue,
  DealFilters,
  DealSort,
  DealPagination,
  DealComponentConfig,
  DealStatistics,
} from '../types/deal.types'
import {
  calculateTotalValue,
  calculateAverageValue,
  calculateConversionRate,
} from '../utils/deal.utils'

export interface DealProviderProps {
  /**
   * Child components
   */
  children: React.ReactNode

  /**
   * API configuration for CRUD operations
   */
  apiConfig?: DealCRUDConfig

  /**
   * Component configuration
   */
  componentConfig?: DealComponentConfig

  /**
   * Initial filters
   */
  initialFilters?: DealFilters

  /**
   * Initial sort configuration
   */
  initialSort?: DealSort[]

  /**
   * Initial pagination
   */
  initialPagination?: DealPagination

  /**
   * Auto-load deals on mount
   */
  autoLoad?: boolean

  /**
   * Enable statistics calculation
   */
  enableStatistics?: boolean

  /**
   * Custom deal transformer
   */
  transformDeal?: (deal: any) => Deal

  /**
   * Event handlers
   */
  onDealCreated?: (deal: Deal) => void
  onDealUpdated?: (deal: Deal) => void
  onDealDeleted?: (dealId: string) => void
  onError?: (error: Error) => void
}

// Create context
const DealContext = createContext<DealContextValue | undefined>(undefined)

/**
 * DealProvider Component
 *
 * @example
 * ```tsx
 * <DealProvider
 *   apiConfig={{
 *     endpoints: {
 *       list: '/api/deals',
 *       create: '/api/deals',
 *       update: '/api/deals/:id',
 *       delete: '/api/deals/:id'
 *     },
 *     apiClient: myApiClient
 *   }}
 *   autoLoad
 * >
 *   <DealList />
 *   <DealForm />
 * </DealProvider>
 * ```
 */
export function DealProvider({
  children,
  apiConfig,
  componentConfig,
  initialFilters = {},
  initialSort = [],
  initialPagination = { page: 1, pageSize: 20 },
  autoLoad = false,
  enableStatistics = false,
  transformDeal,
  onDealCreated,
  onDealUpdated,
  onDealDeleted,
  onError,
}: DealProviderProps) {
  // Use CRUD hook
  const [crudState, crudActions] = useDealCRUD({
    ...apiConfig,
    transformResponse: transformDeal || apiConfig?.transformResponse,
    onError: onError || apiConfig?.onError,
  })

  // Selection state
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set())

  // Statistics
  const [statistics, setStatistics] = useState<DealStatistics | undefined>()

  // Calculate statistics when deals change
  useEffect(() => {
    if (enableStatistics && crudState.deals.length > 0) {
      const stats: DealStatistics = {
        totalDeals: crudState.deals.length,
        totalValue: calculateTotalValue(crudState.deals),
        averageValue: calculateAverageValue(crudState.deals),
        conversionRate: calculateConversionRate(crudState.deals),
        averageTimeToClose: 0, // Would need actual date calculations
        dealsByStatus: {
          open: 0,
          in_progress: 0,
          won: 0,
          lost: 0,
          on_hold: 0,
          archived: 0,
        },
        dealsByStage: {},
        dealsByOwner: {},
      }

      // Calculate distributions
      crudState.deals.forEach((deal) => {
        // By status
        if (deal.status) {
          stats.dealsByStatus[deal.status] =
            (stats.dealsByStatus[deal.status] || 0) + 1
        }

        // By stage
        if (deal.stageId) {
          stats.dealsByStage[deal.stageId] =
            (stats.dealsByStage[deal.stageId] || 0) + 1
        }

        // By owner
        if (deal.ownerId) {
          stats.dealsByOwner[deal.ownerId] =
            (stats.dealsByOwner[deal.ownerId] || 0) + 1
        }
      })

      setStatistics(stats)
    }
  }, [crudState.deals, enableStatistics])

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      crudActions.loadDeals(initialFilters, initialSort, initialPagination)
    }
  }, [])

  // Enhanced CRUD operations with event handlers
  const createDeal = useCallback(
    async (input: CreateDealInput): Promise<Deal> => {
      const deal = await crudActions.createDeal(input)
      if (onDealCreated) {
        onDealCreated(deal)
      }
      return deal
    },
    [crudActions, onDealCreated]
  )

  const updateDeal = useCallback(
    async (input: UpdateDealInput): Promise<Deal> => {
      const deal = await crudActions.updateDeal(input)
      if (onDealUpdated) {
        onDealUpdated(deal)
      }
      return deal
    },
    [crudActions, onDealUpdated]
  )

  const deleteDeal = useCallback(
    async (id: string): Promise<void> => {
      await crudActions.deleteDeal(id)
      if (onDealDeleted) {
        onDealDeleted(id)
      }

      // Remove from selection
      setSelectedDeals((prev) => {
        const newSelection = new Set(prev)
        newSelection.delete(id)
        return newSelection
      })
    },
    [crudActions, onDealDeleted]
  )

  // Selection management
  const selectDeal = useCallback((id: string) => {
    setSelectedDeals((prev) => {
      const newSelection = new Set(prev)
      newSelection.add(id)
      return newSelection
    })
  }, [])

  const deselectDeal = useCallback((id: string) => {
    setSelectedDeals((prev) => {
      const newSelection = new Set(prev)
      newSelection.delete(id)
      return newSelection
    })
  }, [])

  const selectAllDeals = useCallback(() => {
    setSelectedDeals(new Set(crudState.deals.map((d) => d.id)))
  }, [crudState.deals])

  const clearSelection = useCallback(() => {
    setSelectedDeals(new Set())
  }, [])

  // Context value
  const contextValue: DealContextValue = {
    // State
    deals: crudState.deals,
    loading: crudState.loading,
    error: crudState.error,
    filters: crudState.filters,
    sort: crudState.sort,
    pagination: crudState.pagination,
    selectedDeals,
    statistics,

    // Actions
    createDeal,
    updateDeal,
    deleteDeal,
    loadDeals: crudActions.loadDeals,
    setFilters: crudActions.setFilters,
    setSort: crudActions.setSort,
    setPagination: crudActions.setPagination,
    selectDeal,
    deselectDeal,
    selectAllDeals,
    clearSelection,
  }

  return (
    <DealContext.Provider value={contextValue}>{children}</DealContext.Provider>
  )
}

/**
 * Hook to access deal context
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { deals, createDeal, loading } = useDeals()
 *
 *   // Use deal context...
 * }
 * ```
 */
export function useDeals(): DealContextValue {
  const context = useContext(DealContext)

  if (!context) {
    throw new Error('useDeals must be used within a DealProvider')
  }

  return context
}

/**
 * HOC to inject deal context props
 *
 * @example
 * ```tsx
 * const EnhancedComponent = withDeals(MyComponent)
 * ```
 */
export function withDeals<P extends object>(
  Component: React.ComponentType<P & DealContextValue>
): React.ComponentType<P> {
  return function WithDealsComponent(props: P) {
    const dealContext = useDeals()

    return <Component {...props} {...dealContext} />
  }
}
